import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CONFERENCE_FAVORITES_EVENT,
  CONFERENCE_FAVORITES_STORAGE_KEY,
  TEAM_FAVORITES_EVENT,
  TEAM_FAVORITES_STORAGE_KEY,
  getTeamConferenceKey,
  getTeamFavoriteKey,
  isTeamFavorite,
  readConferenceFavorites,
  readTeamFavorites,
  saveTeamFavorites,
} from '../services/favorites';
import {
  getUpcomingScores,
} from '../services/upcomingScores';
import '../styles/upcoming-scoreboard.css';
import { getScores, mergeObservedGames } from '../services/scores.js';
import { normalizeMatchSnapshot, isStaleLiveFeed } from '../data/matchState.js';

export const UPCOMING_LIVE_REFRESH_MS = 30_000;

const FILTERS = [
  {
    value: 'all',
    label: 'All Games',
  },
  {
    value: 'teams',
    label: 'My Teams',
  },
  {
    value: 'conferences',
    label: 'My Conferences',
  },
];

export function prioritizeScheduleGames(games, isFavoriteTeam) {
  const priority = (game) => {
    if ([game.away, game.home].some(isFavoriteTeam)) return 0;
    return [game.away, game.home].some((team) => Number(team?.rank) >= 1 && Number(team?.rank) <= 25) ? 1 : 2;
  };
  // Preserve the existing time/source order within each tier and each day.
  return games.map((game, index) => ({ game, index, priority: priority(game) }))
    .sort((a, b) => a.priority - b.priority || a.index - b.index)
    .map(({ game }) => game);
}

function localISODate() {
  const date =
    new Date();

  return new Date(
    date.getTime() -
      date.getTimezoneOffset() *
        60_000
  )
    .toISOString()
    .slice(0, 10);
}

function formatDateHeading(
  value
) {
  const date =
    new Date(
      `${value}T12:00:00`
    );

  return new Intl.DateTimeFormat(
    'en-US',
    {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }
  ).format(date);
}

function formatDateRange(startDate, endDate) {
  if (!startDate || !endDate) return 'Selected seven-day window';
  const start = new Date(`${startDate}T12:00:00`);
  const end = new Date(`${endDate}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 'Selected seven-day window';
  }
  const startLabel = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric',
  }).format(start);
  const endLabel = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  }).format(end);
  return `${startLabel}–${endLabel}`;
}

function scheduleRank(team) {
  const rank = Number(team?.rank);
  return Number.isInteger(rank) && rank >= 1 && rank <= 25 ? rank : null;
}

function scheduleTeamLabel(team, favorite, fallback) {
  const rank = scheduleRank(team);
  return `${rank ? `No. ${rank} ` : ''}${team?.name || fallback}${favorite ? ' (favorite)' : ''}`;
}

function ScheduleTeam({ team, favorite, fallback }) {
  const rank = scheduleRank(team);
  return (
    <span className="upcoming-compact-team">
      {favorite || rank ? (
        <span className="upcoming-team-markers" aria-hidden="true">
          {favorite ? <span className="upcoming-team-star">★</span> : null}
          {rank ? <span className="upcoming-team-rank">#{rank}</span> : null}
        </span>
      ) : null}
      <strong>{team?.name || fallback}</strong>
    </span>
  );
}

function CompactUpcomingRow({ game, onOpenMatch, isFavoriteTeam }) {
  const snapshot = normalizeMatchSnapshot(game);
  const awayFavorite = isFavoriteTeam(game?.away);
  const homeFavorite = isFavoriteTeam(game?.home);
  const matchupLabel = `${scheduleTeamLabel(game?.away, awayFavorite, 'Away TBA')} versus ${scheduleTeamLabel(game?.home, homeFavorite, 'Home TBA')}`;
  const canOpen = Boolean(game?.gameId && game?.matchCenterAvailable !== false);
  const isFinal = snapshot.status === 'final';
  const isLive = snapshot.status === 'live';
  const awayScore = snapshot.away?.score;
  const homeScore = snapshot.home?.score;
  const scoreLabel = Number.isFinite(awayScore) && Number.isFinite(homeScore)
    ? `${awayScore}-${homeScore}`
    : '';
  const specialLabels = { suspended: 'Suspended', postponed: 'Postponed', canceled: 'Canceled',
    rescheduled: 'Rescheduled', unconfirmed: 'Status unconfirmed' };
  const statusLabel = isLive ? `Live${snapshot.currentSet ? ` · Set ${snapshot.currentSet}` : ''}`
    : isFinal ? 'Final' : specialLabels[snapshot.status] || game?.timeLabel || 'Time TBA';
  const liveDescription = isLive || isFinal || specialLabels[snapshot.status]
    ? `, ${statusLabel}${scoreLabel && (isLive || isFinal) ? `, sets ${scoreLabel}` : ''}` : '';
  const detailLabel = game?.network || game?.venue ||
    (game?.source ? 'Verified schedule' : 'Venue TBA');
  return (
    <button
      type="button"
      className="upcoming-compact-row"
      disabled={!canOpen}
      onClick={() => canOpen && onOpenMatch(game)}
      aria-label={canOpen
        ? `Open ${matchupLabel} in Match Center${liveDescription}`
        : `${matchupLabel}, Match Center available when NCAA publishes the game`}
    >
      <span className={`upcoming-compact-time${isFinal ? ' final' : isLive ? ' live' : ''}`}>
        <span>{statusLabel}</span>
        {(isLive || isFinal) && scoreLabel ? <small>{scoreLabel} sets</small> : null}
      </span>
      <span className="upcoming-compact-teams">
        <ScheduleTeam team={game?.away} favorite={awayFavorite} fallback="Away TBA" />
        <small>{game.neutralSite ? 'vs' : 'at'}</small>
        <ScheduleTeam team={game?.home} favorite={homeFavorite} fallback="Home TBA" />
      </span>
      <span className="upcoming-compact-meta">
        {detailLabel}
      </span>
      <span className="upcoming-compact-arrow" aria-hidden="true">
        {canOpen ? '›' : '—'}
      </span>
    </button>
  );
}

function readEventList({
  event,
  detailKey,
  fallback,
}) {
  const value =
    event?.detail?.[
      detailKey
    ];

  return Array.isArray(value)
    ? value
    : fallback();
}

function LoadingUpcoming() {
  return (
    <div
      className="upcoming-loading-grid"
      aria-label="Loading upcoming matches"
      aria-live="polite"
    >
      {Array.from(
        {
          length: 4,
        },
        (_, index) => (
          <div
            className="score-card skeleton"
            key={index}
            aria-hidden="true"
          >
            <span />
            <span />
            <span />
            <span />
          </div>
        )
      )}
    </div>
  );
}

function UpcomingState({
  symbol,
  title,
  message,
  actionLabel = '',
  onAction = null,
}) {
  return (
    <div className="empty-state upcoming-empty-state">
      <span
        className="state-icon"
        aria-hidden="true"
      >
        {symbol}
      </span>

      <h2>{title}</h2>
      <p>{message}</p>

      {onAction &&
      actionLabel ? (
        <button
          type="button"
          onClick={onAction}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

export default function UpcomingScoreboard({
  sport = 'women',
  startDate = '',
  onOpenMatch,
  refreshKey = 0,
  onRefreshState,
}) {
  const [
    filter,
    setFilter,
  ] = useState('all');

  const [expandedDates, setExpandedDates] = useState([]);

  const [
    teamFavorites,
    setTeamFavorites,
  ] = useState(
    readTeamFavorites
  );

  const [
    conferenceFavorites,
    setConferenceFavorites,
  ] = useState(
    readConferenceFavorites
  );

  const [
    state,
    setState,
  ] = useState({
    loading: true,
    refreshing: false,
    refreshError: '',
    error: '',
    groups: [],
    failedDates: [],
    updatedAt: null,
    startDate: '',
    endDate: '',
  });

  const controllerRef =
    useRef(null);
  const liveControllerRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const rangeRef = useRef('');

  const requestIdRef =
    useRef(0);

  const expansionRangeRef =
    useRef('');

  const loadUpcoming =
    useCallback(({ reset = false } = {}) => {
      controllerRef.current
        ?.abort();
      liveControllerRef.current?.abort();
      liveControllerRef.current = null;

      const controller =
        new AbortController();

      controllerRef.current =
        controller;

      const requestId =
        requestIdRef.current +
        1;

      requestIdRef.current =
        requestId;

      setState(
        (current) => ({
          ...current,
          groups: reset ? [] : current.groups,
          updatedAt: reset ? null : current.updatedAt,
          failedDates: reset ? [] : current.failedDates,
          loading: reset || !current.updatedAt,
          refreshing: !reset && Boolean(current.updatedAt),
          refreshError: '',
          error: '',
        })
      );

      getUpcomingScores({
        sport,
        startDate: /^\d{4}-\d{2}-\d{2}$/.test(startDate)
          ? startDate
          : localISODate(),
        days: 7,
        signal:
          controller.signal,
      })
        .then((payload) => {
          if (
            requestId !==
            requestIdRef.current
          ) {
            return;
          }

          setState((current) => ({
            loading: false,
            refreshing: false,
            refreshError: '',
            error: '',
            groups:
              payload.groups.map(group => {
                const previous = current.groups.find(item => item.date === group.date);
                return group.error && previous ? { ...previous, error: group.error }
                  : { ...group, games: previous ? mergeObservedGames(previous.games, group.games) : group.games };
              }),
            failedDates:
              payload.failedDates,
            updatedAt:
              Date.now(),
            startDate: payload.startDate,
            endDate: payload.endDate,
          }));
        })
        .catch((error) => {
          if (
            error?.name ===
              'AbortError' ||
            requestId !==
              requestIdRef.current
          ) {
            return;
          }

          setState((current) => ({
            ...current,
            loading: false,
            refreshing: false,
            error: current.updatedAt ? '' : (error.message || 'Upcoming matches are temporarily unavailable.'),
            refreshError: current.updatedAt ? 'Refresh failed. Showing the last schedule; scores may be stale.' : '',
          }));
        });
    }, [sport, startDate]);

  useEffect(() => {
    const range = `${sport}:${startDate}`;
    loadUpcoming({ reset: rangeRef.current !== range });
    rangeRef.current = range;

    return () => {
      controllerRef.current
        ?.abort();
      liveControllerRef.current?.abort();
      requestIdRef.current += 1;
    };
  }, [loadUpcoming, refreshKey]);

  useEffect(() => {
    onRefreshState?.({ refreshing: state.loading || state.refreshing,
      refreshError: state.refreshError || state.error,
      updatedLabel: state.updatedAt ? `Updated ${new Date(state.updatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Refresh schedule' });
  }, [onRefreshState, state.loading, state.refreshing, state.refreshError, state.error, state.updatedAt]);

  useEffect(() => {
    async function refreshActiveDates() {
      const current = stateRef.current;
      if (document.visibilityState !== 'visible' || current.loading || current.refreshing || liveControllerRef.current) return;
      const today = localISODate();
      const dates = current.groups.filter(group => group.date === today ||
        group.games.some(game => game.status === 'live' && !isStaleLiveFeed({ ...game, date: group.date }))).map(group => group.date);
      if (!dates.length) return;
      const controller = new AbortController();
      liveControllerRef.current = controller;
      const requestId = requestIdRef.current;
      setState(value => ({ ...value, refreshing: true }));
      try {
        const results = await Promise.all(dates.map(async date => {
          try {
            const payload = await getScores({ date, sport, signal: controller.signal });
            return { date, payload, failed: Boolean(payload.stale) };
          } catch { return { date, failed: true }; }
        }));
        if (controller.signal.aborted || requestId !== requestIdRef.current) return;
        const updates = new Map(results.filter(item => !item.failed).map(item => [item.date, item.payload.games]));
        const failed = results.some(item => item.failed);
        setState(value => ({ ...value, refreshing: false,
          groups: value.groups.map(group => updates.has(group.date)
            ? { ...group, error: '', games: mergeObservedGames(group.games, updates.get(group.date)) } : group),
          failedDates: value.failedDates.filter(date => !updates.has(date)),
          updatedAt: failed ? value.updatedAt : Date.now(),
          refreshError: failed ? 'Some live scores could not refresh. Showing the last available results.' : '',
        }));
      } finally {
        if (liveControllerRef.current === controller) liveControllerRef.current = null;
      }
    }
    const timer = window.setInterval(refreshActiveDates, UPCOMING_LIVE_REFRESH_MS);
    document.addEventListener('visibilitychange', refreshActiveDates);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshActiveDates);
      liveControllerRef.current?.abort();
      liveControllerRef.current = null;
    };
  }, [sport, startDate]);

  useEffect(() => {
    function handleTeams(
      event
    ) {
      setTeamFavorites(
        readEventList({
          event,
          detailKey:
            'favorites',
          fallback:
            readTeamFavorites,
        })
      );
    }

    function handleConferences(
      event
    ) {
      setConferenceFavorites(
        readEventList({
          event,
          detailKey:
            'conferenceFavorites',
          fallback:
            readConferenceFavorites,
        })
      );
    }

    function handleStorage(
      event
    ) {
      if (
        !event ||
        event.key ===
          TEAM_FAVORITES_STORAGE_KEY
      ) {
        setTeamFavorites(
          readTeamFavorites()
        );
      }

      if (
        !event ||
        event.key ===
          CONFERENCE_FAVORITES_STORAGE_KEY
      ) {
        setConferenceFavorites(
          readConferenceFavorites()
        );
      }
    }

    window.addEventListener(
      TEAM_FAVORITES_EVENT,
      handleTeams
    );

    window.addEventListener(
      CONFERENCE_FAVORITES_EVENT,
      handleConferences
    );

    window.addEventListener(
      'storage',
      handleStorage
    );

    return () => {
      window.removeEventListener(
        TEAM_FAVORITES_EVENT,
        handleTeams
      );

      window.removeEventListener(
        CONFERENCE_FAVORITES_EVENT,
        handleConferences
      );

      window.removeEventListener(
        'storage',
        handleStorage
      );
    };
  }, []);

  const teamFavoriteSet =
    useMemo(
      () =>
        new Set(
          teamFavorites
        ),
      [teamFavorites]
    );

  const conferenceFavoriteSet =
    useMemo(
      () =>
        new Set(
          conferenceFavorites
        ),
      [conferenceFavorites]
    );

  const isFavoriteTeam =
    useCallback(
      (team) =>
        isTeamFavorite(
          team,
          teamFavoriteSet
        ),
      [teamFavoriteSet]
    );

  const matchesFilter =
    useCallback(
      (game) => {
        if (
          filter === 'teams'
        ) {
          return (
            isFavoriteTeam(
              game?.away
            ) ||
            isFavoriteTeam(
              game?.home
            )
          );
        }

        if (
          filter ===
          'conferences'
        ) {
          return [
            game?.away,
            game?.home,
          ].some((team) => {
            const key =
              getTeamConferenceKey(
                team
              );

            return Boolean(
              key &&
              conferenceFavoriteSet.has(
                key
              )
            );
          });
        }

        return true;
      },
      [
        conferenceFavoriteSet,
        filter,
        isFavoriteTeam,
      ]
    );

  const visibleGroups =
    useMemo(
      () =>
        state.groups
          .map((group) => ({
            ...group,
            games:
              prioritizeScheduleGames(group.games.filter(matchesFilter), isFavoriteTeam),
          }))
          .filter(
            (group) =>
              group.games.length
          ),
      [
        matchesFilter,
        isFavoriteTeam,
        state.groups,
      ]
    );

  const allGameCount =
    useMemo(
      () =>
        state.groups.reduce(
          (
            total,
            group
          ) =>
            total +
            group.games.length,
          0
        ),
      [state.groups]
    );

  const visibleGameCount =
    useMemo(
      () =>
        visibleGroups.reduce(
          (
            total,
            group
          ) =>
            total +
            group.games.length,
          0
        ),
      [visibleGroups]
    );

  const visibleGroupKey = useMemo(
    () => visibleGroups
      .map((group) => `${group.date}:${group.games.length}`)
      .join('|'),
    [visibleGroups]
  );

  useEffect(() => {
    const dates = visibleGroups.map((group) => group.date);
    const dateSet = new Set(dates);
    const rangeKey = `${state.startDate}:${state.endDate}`;
    const rangeChanged = expansionRangeRef.current !== rangeKey;

    expansionRangeRef.current = rangeKey;

    if (!dates.length) {
      setExpandedDates([]);
      return;
    }

    setExpandedDates((current) => {
      if (filter !== 'all') {
        return dates;
      }

      const next = new Set(
        rangeChanged
          ? []
          : current.filter((date) => dateSet.has(date))
      );

      visibleGroups.forEach((group) => {
        const hasLiveMatch = group.games.some((game) => game?.status === 'live');
        if (hasLiveMatch || group.date === localISODate()) {
          next.add(group.date);
        }
      });

      if (!next.size) {
        next.add(dates[0]);
      }

      return Array.from(next);
    });
  }, [filter, state.endDate, state.startDate, visibleGroupKey]);

  function toggleDate(date) {
    setExpandedDates((current) => current.includes(date)
      ? current.filter((item) => item !== date)
      : [...current, date]
    );
  }

  function toggleTeamFavorite(
    team
  ) {
    const key =
      getTeamFavoriteKey(
        team
      );

    if (!key) {
      return;
    }

    setTeamFavorites(
      (current) => {
        const next =
          current.includes(key)
            ? current.filter(
                (item) =>
                  item !== key
              )
            : [
                ...current,
                key,
              ];

        saveTeamFavorites(
          next
        );

        return next;
      }
    );
  }

  function openStandings() {
    const url =
      new URL(
        window.location.href
      );

    url.searchParams.delete(
      'match'
    );

    url.hash =
      'standings';

    window.location.assign(
      url.toString()
    );
  }

  if (state.loading) {
    return (
      <LoadingUpcoming />
    );
  }

  if (state.error) {
    return (
      <UpcomingState
        symbol="!"
        title="We couldn't load upcoming matches."
        message={state.error}
        actionLabel="Retry"
        onAction={
          loadUpcoming
        }
      />
    );
  }

  return (
    <div className="upcoming-scoreboard">
      {state.refreshError ? <p className="upcoming-partial-note" role="status">{state.refreshError}</p> : null}
      <div className="upcoming-controls">
        <div
          className="upcoming-filter"
          role="group"
          aria-label="Filter upcoming matches"
        >
          {FILTERS.map(
            (item) => (
              <button
                type="button"
                key={
                  item.value
                }
                className={
                  filter ===
                  item.value
                    ? 'active'
                    : ''
                }
                aria-pressed={
                  filter ===
                  item.value
                }
                onClick={() =>
                  setFilter(
                    item.value
                  )
                }
              >
                <span>
                  {item.label}
                </span>

                {item.value ===
                'teams' ? (
                  <strong>
                    {
                      teamFavorites.length
                    }
                  </strong>
                ) : item.value ===
                  'conferences' ? (
                  <strong>
                    {
                      conferenceFavorites.length
                    }
                  </strong>
                ) : null}
              </button>
            )
          )}
        </div>

        <div className="upcoming-result-summary">
          <strong>
            {visibleGameCount}{' '}
            {visibleGameCount ===
            1
              ? 'match'
              : 'matches'}
          </strong>

          <span>
            {formatDateRange(state.startDate, state.endDate)}
          </span>
        </div>
      </div>

      {state.failedDates
        .length ? (
        <div
          className="upcoming-partial-note"
          role="status"
        >
          Some dates could not
          refresh. Showing all
          available upcoming
          matches.
        </div>
      ) : null}

      {!allGameCount ? (
        <UpcomingState
          symbol="VW"
          title={state.failedDates.length ? 'The schedule is temporarily incomplete.' : 'No matches are scheduled in the next seven days.'}
          message={state.failedDates.length ? 'Some dates could not be checked. This does not mean there are no matches.' : 'The college volleyball season may be between schedules. Upcoming matches will appear here automatically when they are published.'}
          actionLabel={state.failedDates.length ? 'Retry schedule' : undefined}
          onAction={state.failedDates.length ? loadUpcoming : undefined}
        />
      ) : !visibleGroups
          .length ? (
        filter ===
        'conferences' &&
        !conferenceFavorites
          .length ? (
          <UpcomingState
            symbol="★"
            title="No favorite conferences yet."
            message="Save conferences from the Standings page, then their upcoming matches will appear here."
            actionLabel="Choose conferences"
            onAction={
              openStandings
            }
          />
        ) : (
          <UpcomingState
            symbol="0"
            title="No upcoming matches fit this filter."
            message={
              filter ===
              'teams'
                ? teamFavorites.length ? 'Your saved teams are not scheduled during this seven-day window.' : 'You have no saved teams yet. Save a team from the Teams page to see its matches here.'
                : 'Your saved conferences have no matches scheduled during this seven-day window.'
            }
            actionLabel="Show all games"
            onAction={() =>
              setFilter('all')
            }
          />
        )
      ) : (
        <div
          className="upcoming-date-list"
          aria-live="polite"
        >
          {visibleGroups.map(
            (group) => {
              const expanded = expandedDates.includes(group.date);
              const headingId = `upcoming-${group.date}`;
              const panelId = `${headingId}-matches`;

              return (
              <section
                className={`upcoming-date-group${expanded ? ' expanded' : ''}`}
                key={
                  group.date
                }
                aria-labelledby={headingId}
              >
                <button
                  type="button"
                  className="upcoming-date-heading"
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  onClick={() => toggleDate(group.date)}
                >
                  <span className="upcoming-date-title">
                    <span className="eyebrow">Seven-day schedule</span>
                    <span className="upcoming-date-name" id={headingId}>
                      {formatDateHeading(group.date)}
                    </span>
                  </span>

                  <span className="upcoming-date-summary">
                    <strong>{group.games.length}</strong>
                    <span className="upcoming-date-chevron" aria-hidden="true">⌄</span>
                  </span>
                </button>

                {expanded ? (
                  <div
                    className="upcoming-compact-list"
                    id={panelId}
                    role="region"
                    aria-labelledby={headingId}
                  >
                    {group.games.map(
                      (game) => (
                        <CompactUpcomingRow
                          key={`${group.date}:${game.gameId || game.id}`}
                          game={game}
                          onOpenMatch={onOpenMatch}
                          isFavoriteTeam={isFavoriteTeam}
                        />
                      )
                    )}
                  </div>
                ) : null}
              </section>
              );
            }
          )}
        </div>
      )}
    </div>
  );
}
